import {
  isBreathworkAccessible as _isBreathworkAccessible,
  isJourneyAccessible as _isJourneyAccessible,
  type AccessContext,
} from '@breath-lab/domain';
import { useBreathworks } from './useBreathworks';
import { useJourneys } from './useJourneys';
import { useSubscription } from './useSubscription';
import { useTrialState } from './useTrialState';

export interface AccessGateState {
  ctx: AccessContext;
  isBreathworkAccessible: (breathworkId: string) => boolean;
  isJourneyAccessible: (journeyId: string) => boolean;
}

/**
 * Platform-neutral access selectors. Combines the catalog + trial + premium
 * state into a derived AccessContext. App-side wrappers add the navigation /
 * paywall side effects (web uses react-router; RN will use expo-router).
 */
export function useAccessGateState(): AccessGateState {
  const { freeIds: freeBreathworkIds, paywallEnabled } = useBreathworks();
  const { freeIds: freeJourneyIds } = useJourneys();
  const { isInTrial } = useTrialState();
  const { isPremium } = useSubscription();

  const ctx: AccessContext = {
    paywallEnabled,
    freeBreathworkIds,
    freeJourneyIds,
    hasFullAccess:
      isPremium ||
      isInTrial ||
      (import.meta as { env?: Record<string, string | undefined> } | undefined)?.env
        ?.VITE_DISABLE_UI_PAYWALL === 'true' ||
      (typeof process !== 'undefined' && process.env.EXPO_PUBLIC_DISABLE_UI_PAYWALL === 'true'),
  };

  return {
    ctx,
    isBreathworkAccessible: (breathworkId: string) =>
      _isBreathworkAccessible(breathworkId, ctx),
    isJourneyAccessible: (journeyId: string) =>
      _isJourneyAccessible(journeyId, ctx),
  };
}
